import { useState, useEffect } from 'react';
import { workflowAPI } from '../services/api';

interface WorkflowCounts {
  waitingPatients: number;
  triagePatients: number;
  scheduledPatients: number;
}

interface UseWorkflowCountsReturn {
  counts: WorkflowCounts;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useWorkflowCounts = (): UseWorkflowCountsReturn => {
  const [counts, setCounts] = useState<WorkflowCounts>({
    waitingPatients: 0,
    triagePatients: 0,
    scheduledPatients: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCounts = async () => {
    try {
      setLoading(true);
      setError(null);

      const [waitingResponse, triageResponse, scheduledResponse] = await Promise.allSettled([
        workflowAPI.getWaitingPatientsCount(),
        workflowAPI.getTriagePatientsCount(),
        workflowAPI.getScheduledPatientsCount().catch((err) => {
          // Silently handle 404 for scheduled endpoint if backend hasn't been restarted yet
          if (err.response?.status === 404) {
            return { data: { count: 0 } };
          }
          throw err;
        })
      ]);

      setCounts({
        waitingPatients: waitingResponse.status === 'fulfilled' ? (waitingResponse.value.data.count || 0) : 0,
        triagePatients: triageResponse.status === 'fulfilled' ? (triageResponse.value.data.count || 0) : 0,
        scheduledPatients: scheduledResponse.status === 'fulfilled' ? (scheduledResponse.value.data.count || 0) : 0
      });
    } catch (err: any) {
      console.error('Error fetching workflow counts:', err);
      setError(err.response?.data?.message || 'Failed to fetch counts');
      // Set fallback counts to 0 on error
      setCounts({
        waitingPatients: 0,
        triagePatients: 0,
        scheduledPatients: 0
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCounts();

    // Set up interval to refresh counts every 30 seconds
    const interval = setInterval(fetchCounts, 30000);

    return () => clearInterval(interval);
  }, []);

  return {
    counts,
    loading,
    error,
    refetch: fetchCounts
  };
};
